import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Bell, Package, DollarSign, BookOpen, Eye, CheckCircle, Trash2,
  Plus, Edit, BarChart3,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import ScheduleManager from "../components/ScheduleManager";
import ReminderManager from "../components/ReminderManager";
import InquiriesManager from "../components/InquiriesManager";
import AnalyticsManager from "../components/AnalyticsManager";
import CourseEditor from "../components/CourseEditor";
import { CohortRegistrations } from "../components/CohortRegistrations";
import { ProformaManager } from "../components/ProformaManager";
import CoursePreview from "../components/CoursePreview";
import ServiceRequestsManager from "../components/ServiceRequestsManager";
import PromoCodeManager from "../components/PromoCodeManager";
import BlogManager from "../components/BlogManager";
import ResourcesManager from "../components/ResourcesManager";
import MediaScraperManager from "../components/MediaScraperManager";
import PaymentVerificationManager from "../components/PaymentVerificationManager";
import AdminSidebar from "../components/AdminSidebar";
import AdminTopbar from "../components/AdminTopbar";
import GlobalSearch from "../components/GlobalSearch";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_META = {
  analytics: { title: "Analytics", description: "Revenue, orders & operations at a glance" },
  notifications: { title: "Notifications", description: "All system notifications" },
  orders: { title: "Orders & Payments", description: "Verify payments and manage enrollment" },
  proformas: { title: "Pro Forma Invoices", description: "Review, issue and revoke corporate pro formas" },
  inquiries: { title: "Inquiries", description: "Consulting & in-house training requests" },
  "service-requests": { title: "Service Requests", description: "Customer-submitted service forms" },
  schedules: { title: "Schedules", description: "Course delivery calendar" },
  reminders: { title: "Reminders", description: "Scheduled email campaigns" },
  "promo-codes": { title: "Promo Codes", description: "Discounts and coupons" },
  courses: { title: "Course Management", description: "Create, edit, and publish courses" },
  blog: { title: "Blog", description: "News and insights articles" },
  resources: { title: "Resources", description: "In the Media — manual entries" },
  scraper: { title: "Media Scraper", description: "Automated news article scraping" },
};

const AdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(searchParams.get("section") || "analytics");
  useEffect(() => {
    const s = searchParams.get("section");
    if (s) setActiveSection(s);
  }, [searchParams]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("admin-sidebar-collapsed") === "1");
  const [searchOpen, setSearchOpen] = useState(false);

  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [courses, setCourses] = useState([]);
  const [inquiryCounts, setInquiryCounts] = useState({ consulting: 0, training: 0 });
  const [loading, setLoading] = useState(true);
  const [showCourseEditor, setShowCourseEditor] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showCoursePreview, setShowCoursePreview] = useState(false);
  const [previewCourse, setPreviewCourse] = useState(null);
  const [courseSearch, setCourseSearch] = useState("");

  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const authHeader = getAuthHeader();
      const [statsRes, ordersRes, notificationsRes, coursesRes, consultingRes, trainingRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers: authHeader }),
        axios.get(`${API}/orders`, { headers: authHeader }),
        axios.get(`${API}/notifications`, { headers: authHeader }),
        axios.get(`${API}/courses?all=true`),
        axios.get(`${API}/consulting-inquiries`, { headers: authHeader }).catch(() => ({ data: { inquiries: [] } })),
        axios.get(`${API}/inhouse-training-inquiries`, { headers: authHeader }).catch(() => ({ data: { inquiries: [] } })),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
      setNotifications(notificationsRes.data.notifications);
      setCourses(coursesRes.data);
      const newConsulting = consultingRes.data.inquiries?.filter((i) => !i.status || i.status === "new").length || 0;
      const newTraining = trainingRes.data.inquiries?.filter((i) => !i.status || i.status === "new").length || 0;
      setInquiryCounts({ consulting: newConsulting, training: newTraining });
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, logout, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keep URL in sync with section, for linkability
  const changeSection = (s) => {
    setActiveSection(s);
    setSearchParams({ section: s }, { replace: true });
  };

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem("admin-sidebar-collapsed", next ? "1" : "0");
  };

  // Keyboard shortcuts: / or Cmd/Ctrl+K opens search; g o / g a / g n jumps
  useEffect(() => {
    let gPressed = false;
    let gTimer = null;
    const isTyping = (ev) => {
      const el = ev.target;
      return el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    };
    const handler = (ev) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "k") {
        ev.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (isTyping(ev)) return;
      if (ev.key === "/" && !searchOpen) {
        ev.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (ev.key.toLowerCase() === "g" && !gPressed) {
        gPressed = true;
        clearTimeout(gTimer);
        gTimer = setTimeout(() => { gPressed = false; }, 800);
        return;
      }
      if (gPressed) {
        const k = ev.key.toLowerCase();
        const jump = { o: "orders", a: "analytics", n: "notifications", i: "inquiries", c: "courses", r: "reminders", b: "blog", s: "schedules" }[k];
        if (jump) {
          ev.preventDefault();
          changeSection(jump);
        }
        gPressed = false;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOpen]);

  const markNotificationRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() });
      setNotifications((ns) => ns.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch {
      toast.error("Failed to update notification");
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() });
      setNotifications((ns) => ns.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Failed to update notifications");
    }
  };

  const clearAllNotifications = async () => {
    if (!window.confirm("Are you sure you want to delete all notifications? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/notifications/clear-all`, { headers: getAuthHeader() });
      setNotifications([]);
      toast.success("All notifications cleared");
      fetchData();
    } catch {
      toast.error("Failed to clear notifications");
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course?")) return;
    try {
      await axios.delete(`${API}/courses/${courseId}`, { headers: getAuthHeader() });
      toast.success("Course deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete course");
    }
  };

  const filteredCourses = courses.filter((course) =>
    course.title?.toLowerCase().includes(courseSearch.toLowerCase()) ||
    course.category?.toLowerCase().includes(courseSearch.toLowerCase())
  );

  const resetTestData = async () => {
    if (!window.confirm("⚠️ WARNING: This will permanently delete ALL orders, enquiries, and notifications. This cannot be undone.\n\nAre you sure you want to reset all test data?")) return;
    try {
      const response = await axios.delete(`${API}/admin/reset-test-data`, { headers: getAuthHeader() });
      toast.success(`Test data reset: ${response.data.deleted.orders} orders, ${response.data.deleted.consulting_inquiries + response.data.deleted.training_inquiries} enquiries deleted`);
      fetchData();
    } catch {
      toast.error("Failed to reset test data");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  // Sidebar badges
  const actionItemCount = orders.filter((o) => o.status === "proof_submitted" || o.status === "needs_clarification").length;
  const sidebarBadges = {
    unread_notifications: stats?.unread_notifications || 0,
    action_items: actionItemCount,
    inquiries: inquiryCounts.consulting + inquiryCounts.training,
  };

  const sectionMeta = SECTION_META[activeSection] || { title: "Admin", description: "" };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminSidebar
        activeSection={activeSection}
        onSectionChange={changeSection}
        badges={sidebarBadges}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebar}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        <AdminTopbar
          sectionTitle={sectionMeta.title}
          sectionDescription={sectionMeta.description}
          onOpenSearch={() => setSearchOpen(true)}
          onRefresh={fetchData}
          onResetTestData={resetTestData}
          onLogout={handleLogout}
          notifications={notifications}
          unreadCount={stats?.unread_notifications || 0}
          onMarkAllRead={markAllRead}
          onMarkRead={markNotificationRead}
          onOpenAllNotifications={() => changeSection("notifications")}
          user={user}
        />

        <main className="flex-1 p-6 lg:p-8 overflow-x-auto" data-testid="admin-content">
          {/* Section content */}
          {loading && activeSection !== "analytics" ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}><CardContent className="p-6">
                  <div className="h-4 w-1/2 skeleton rounded mb-2" />
                  <div className="h-8 w-3/4 skeleton rounded" />
                </CardContent></Card>
              ))}
            </div>
          ) : (
            <>
              {activeSection === "analytics" && (
                <>
                  {/* Top-level KPI cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <QuickStat title="Total Courses" value={stats?.total_courses || 0} icon={BookOpen} color="text-brand" />
                    <QuickStat title="Total Orders" value={stats?.total_orders || 0} icon={Package} color="text-accent" />
                    <QuickStat title="Total Revenue" value={`$${Number(stats?.total_revenue || 0).toLocaleString()}`} icon={DollarSign} color="text-green-600" />
                    <QuickStat title="Unread Alerts" value={stats?.unread_notifications || 0} icon={Bell} color="text-orange-500" />
                  </div>
                  <AnalyticsManager />
                </>
              )}

              {activeSection === "notifications" && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading">Recent Notifications</CardTitle>
                    <div className="flex gap-2">
                      {notifications.some((n) => !n.is_read) && (
                        <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="notif-mark-all-read">
                          <CheckCircle className="h-4 w-4 mr-2" /> Mark All Read
                        </Button>
                      )}
                      {notifications.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearAllNotifications} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4 mr-2" /> Clear All
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {notifications.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No notifications yet</p>
                    ) : (
                      <div className="space-y-4">
                        {notifications.map((n) => (
                          <div key={n.id} className={`flex items-start justify-between p-4 rounded-lg border ${n.is_read ? "bg-white" : "bg-blue-50 border-blue-100"}`} data-testid={`notification-${n.id}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{n.title}</h4>
                                {!n.is_read && <span className="w-2 h-2 rounded-full bg-brand" />}
                              </div>
                              <p className="text-sm text-muted-foreground">{n.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                            </div>
                            {!n.is_read && <Button variant="ghost" size="sm" onClick={() => markNotificationRead(n.id)}><Eye className="h-4 w-4" /></Button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {activeSection === "orders" && (
                <Card>
                  <CardContent className="p-6">
                    <PaymentVerificationManager orders={orders} onRefresh={fetchData} getAuthHeader={getAuthHeader} />
                  </CardContent>
                </Card>
              )}

              {activeSection === "proformas" && <ProformaManager getAuthHeader={getAuthHeader} />}
              {activeSection === "inquiries" && (<Card><CardContent className="p-6"><InquiriesManager /></CardContent></Card>)}
              {activeSection === "service-requests" && (<Card><CardContent className="p-6"><ServiceRequestsManager getAuthHeader={getAuthHeader} /></CardContent></Card>)}
              {activeSection === "schedules" && (<Card><CardContent className="p-6"><ScheduleManager /></CardContent></Card>)}
              {activeSection === "reminders" && (<Card><CardContent className="p-6"><ReminderManager /></CardContent></Card>)}
              {activeSection === "promo-codes" && <PromoCodeManager />}
              {activeSection === "blog" && <BlogManager />}
              {activeSection === "resources" && (<Card><CardContent className="p-6"><ResourcesManager /></CardContent></Card>)}
              {activeSection === "scraper" && (<Card><CardContent className="p-6"><MediaScraperManager /></CardContent></Card>)}

              {activeSection === "courses" && (
                <>
                  <CohortRegistrations getAuthHeader={getAuthHeader} />
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                      <div>
                        <CardTitle className="font-heading">Course Management</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">{courses.length} courses total</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          placeholder="Search courses..."
                          value={courseSearch}
                          onChange={(e) => setCourseSearch(e.target.value)}
                          className="w-64"
                        />
                        <Button
                          variant="outline"
                          className="border-brand text-brand"
                          data-testid="sync-catalog-btn"
                          onClick={async () => {
                            try {
                              const r = await axios.post(`${API}/courses/sync-catalog`, null, { headers: getAuthHeader() });
                              toast.success(`Catalog synced — ${r.data.renamed.length} renamed, ${r.data.inserted_count} added, ${r.data.content_refreshed_count} refreshed`);
                              fetchData();
                            } catch { toast.error("Catalog sync failed"); }
                          }}
                        >
                          Sync PECB Catalog
                        </Button>
                        <Button onClick={() => { setEditingCourse(null); setShowCourseEditor(true); }} className="bg-brand hover:bg-brand-dark text-white">
                          <Plus className="h-4 w-4 mr-2" /> Add New Course
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {filteredCourses.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                          {courseSearch ? "No courses match your search" : "No courses yet"}
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b bg-slate-50">
                                <th className="text-left py-3 px-4 font-medium">Course</th>
                                <th className="text-left py-3 px-4 font-medium">Category</th>
                                <th className="text-left py-3 px-4 font-medium">Level</th>
                                <th className="text-left py-3 px-4 font-medium">Price Range</th>
                                <th className="text-left py-3 px-4 font-medium">Status</th>
                                <th className="text-left py-3 px-4 font-medium">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredCourses.map((course) => (
                                <tr key={course.id} className="border-b hover:bg-slate-50" data-testid={`course-row-${course.id}`}>
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-12 h-12 rounded-lg bg-cover bg-center flex-shrink-0 bg-slate-200" style={{ backgroundImage: course.image_url ? `url(${course.image_url})` : "none" }} />
                                      <div>
                                        <p className="font-medium text-sm">{course.title}</p>
                                        {course.is_featured && <Badge variant="outline" className="text-xs">Featured</Badge>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-sm">{course.category}</td>
                                  <td className="py-3 px-4 text-sm">{course.certification_level || course.level || "-"}</td>
                                  <td className="py-3 px-4 text-sm">
                                    {course.delivery_formats?.length ? (
                                      <>${Math.min(...course.delivery_formats.map((f) => f.price || 0))} - ${Math.max(...course.delivery_formats.map((f) => f.price || 0))}</>
                                    ) : course.price ? `$${course.price}` : "-"}
                                  </td>
                                  <td className="py-3 px-4">
                                    <Badge className={course.is_active !== false ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                                      {course.is_active !== false ? "Active" : "Inactive"}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex gap-1">
                                      <Button variant="ghost" size="sm" onClick={() => { setPreviewCourse(course); setShowCoursePreview(true); }} title="Preview" data-testid={`preview-course-${course.id}`}><Eye className="h-4 w-4" /></Button>
                                      <Button variant="ghost" size="sm" onClick={() => { setEditingCourse(course); setShowCourseEditor(true); }} className="text-brand" title="Edit" data-testid={`edit-course-${course.id}`}><Edit className="h-4 w-4" /></Button>
                                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteCourse(course.id)} title="Delete" data-testid={`delete-course-${course.id}`}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <CourseEditor open={showCourseEditor} onOpenChange={setShowCourseEditor} course={editingCourse} onSave={fetchData} getAuthHeader={getAuthHeader} />
                  <CoursePreview open={showCoursePreview} onOpenChange={setShowCoursePreview} course={previewCourse} />
                </>
              )}
            </>
          )}
        </main>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} onSelectSection={changeSection} />
    </div>
  );
};

const QuickStat = ({ title, value, icon: Icon, color }) => (
  <Card data-testid={`stat-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="font-heading font-bold text-2xl mt-1">{value}</p>
        </div>
        <div className={`p-2.5 rounded-full bg-slate-100 ${color}`}><Icon className="h-5 w-5" /></div>
      </div>
    </CardContent>
  </Card>
);

export default AdminDashboard;
